import { Formik } from 'formik'
import PropTypes from 'prop-types'
import { useState, useRef } from 'react'
import { connect } from 'react-redux'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'

import { TIMEZONES } from '../../../helpers/constants'
import { getAssetLink } from '../../../helpers/get-asset-link'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import {
  createBuilding,
  selectBuildingById,
  selectBuildings,
  updateBuilding,
  updateBuildingApartmentsId,
} from '../../../reducers/buildingSelectorReducer'
import API from '../../../api/api'
import ConfirmPopup from '../../Popups/ConfirmPopup'
import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import FormFieldTags from '../../FormLegacy/FormFieldTags/FormFieldTags'
import { formValidation } from '../../FormLegacy/validation'
import Icon from '../../Icons'

import ApartmentsIdField from './components/ApartmentsIdField'
import ConvenienenceFeeField from './components/ConvenienceFeeField'
import LockerSelect from './components/LockerSelect'
import { getIglooWorkStaffAccessAvailablePatch } from './helpers'
import { useCustomPinSettingTracking } from './custom-pin-setting.hooks'
import { ZIP_CODE_CA_REGEX } from '@/constants/regex'

// Fields that must be false whenever keylessAccessAvailable is off
const KEYLESS_ACCESS_DEPENDENT_FIELDS = [
  'keylessAccessForStaffAvailable',
  'butterflyAvailable',
  'bmxInAppCallsAvailable',
  'butterflyVisitorAccessAvailable',
  'latchAvailable',
  'brivoAvailable',
  'brivoVisitorAccessAvailable',
  'brivoMagicButtonAvailable',
  'openpathAvailable',
  'twoNAvailable',
  'dormakabaAvailable',
  'saltoKSAvailable',
  'saltoSpaceAvailable',
  'saltoSpaceUserSyncAvailable',
  'saltoSpaceVisitorAccessAvailable',
  'saltoSpaceDeviceBindingAvailable',
  'swiftlaneAvailable',
  'swiftlaneVisitorAccessAvailable',
  'swiftlaneVoipAvailable',
  'swiftlaneAutoUserAvailable',
  'akuvoxAvailable',
  'rentlyAvailable',
  'remoteLockAvailable',
  'remoteLockFobAvailable',
  'remoteLockVisitorAccessAvailable',
  'accentraAvailable',
  'pointCentralAvailable',
  'pointCentralLocksAvailable',
  'pointCentralThermostatsAvailable',
  'iglooWorkAvailable',
  'iglooWorkVisitorAccessAvailable',
  'iglooWorkAlgoPinAvailable',
  'iglooWorkCustomPinAvailable',
  'iglooWorkStaffAccessAvailable',
  'visionlineAvailable',
  'iglooWorkStaffMasterKeyAvailable',
]

const formatCanadianPostalCode = (value = '') => {
  const compact = value.replace(/\s+/g, '').toUpperCase()
  return `${compact.slice(0, 3)} ${compact.slice(3)}`
}

const OrganisationBuildingForm = ({
  selectedBuilding,
  buildings,
  createBuilding,
  updateBuilding,
  updateBuildingApartmentsId,
  params,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const [formError, setFormError] = useState('')
  const [isSyncingSaltoSpace, setIsSyncingSaltoSpace] = useState(false)
  const [showIglooWorkVisitorAccessConfirm, setShowIglooWorkVisitorAccessConfirm] = useState(false)
  // Holds the value the staff toggle is about to move to while the confirm popup is open, so the
  // popup can render copy for the right direction. `null` means no confirm is pending.
  const [showIglooWorkStaffMasterKeyConfirm, setShowIglooWorkStaffMasterKeyConfirm] =
    useState(false)
  // Holds the value the staff toggle is about to move to while the confirm popup is open.
  const [pendingKeylessStaffAccess, setPendingKeylessStaffAccess] = useState(null)
  const { displaySnackbar } = useSnackbar()

  const [accentraCredentials, setAccentraCredentials] = useState({ login: '', password: '' })
  const [isLoading, setLoading] = useState(false)
  const formRef = useRef(null)

  const buildingId = selectedBuilding.id
  const isNew = params.buildingId && params.buildingId === 'new'
  const customPinTracking = useCustomPinSettingTracking({
    editable: Boolean(
      selectedBuilding.keylessAccessAvailable && selectedBuilding.iglooWorkAvailable,
    ),
    persistedValue: Boolean(selectedBuilding.iglooWorkCustomPinAvailable),
    propertyId: buildingId,
  })

  const handleFormSubmit = async (
    {
      logo,
      image,
      address,
      securityDispatch,
      onsiteRetailLink,
      link,
      iosAppStoreLink,
      googlePlayLink,
      emergencyPhone,
      code,
      hubspotDealId,
      residentConvenienceFeeRate,
      id,
      active,
      name,
      city,
      email,
      appName,
      keylessAccessAvailable,
      state,
      country,
      phone,
      postalCode,
      buildingResidence,
      isHoa,
      avatarChangeRestricted,
      nameChangeRestricted,
      servicesAvailable,
      buildingEssentialsAvailable,
      perksAvailable,
      mealMeAvailable,
      groupsAvailable,
      tvAppAvailable,
      packageRoomAvailable,
      rewardsAvailable,
      doorActivityNotificationsAvailable,
      valetAvailable,
      marketShopAvailable,
      marketShopRestaurantAvailable,
      marketplacePostsReview,
      amenityReservations,
      amenitiesAcrAvailable,
      communityCalendar,
      timezone,
      guestAccess,
      physicalKeyAvailable,
      customAccessAvailable,
      maintenanceRequestAvailable,
      manualApproval,
      groupsPostReview,
      communicationAvailable,
      checklistsAvailable,
      chatAvailable,
      widgetChatAvailable,
      homepageAvailable,
      chatPM,
      chatFD,
      chatMT,
      packagesAvailable,
      packagesQuickScanAvailable,
      packagesD2DAvailable,
      communicateSMSAvailable,
      buildingAnalyticsAvailable,
      managementUsersAvailable,
      latchAvailable,
      brivoAvailable,
      openpathAvailable,
      dormakabaAvailable,
      saltoKSAvailable,
      saltoSpaceAvailable,
      saltoSpaceUserSyncAvailable,
      saltoSpaceVisitorAccessAvailable,
      saltoSpacePinVisitorAccessAvailable,
      saltoSpaceFobsAvailable,
      brivoVisitorAccessAvailable,
      brivoMagicButtonAvailable,
      akuvoxAvailable,
      rentlyAvailable,
      swiftlaneAvailable,
      swiftlaneVisitorAccessAvailable,
      swiftlaneVoipAvailable,
      swiftlaneAutoUserAvailable,
      remoteLockAvailable,
      remoteLockFobAvailable,
      remoteLockVisitorAccessAvailable,
      accentraAvailable,
      pointCentralAvailable,
      pointCentralThermostatsAvailable,
      pointCentralLocksAvailable,
      iglooWorkAvailable,
      iglooWorkVisitorAccessAvailable,
      iglooWorkAlgoPinAvailable,
      iglooWorkCustomPinAvailable,
      // The server value at load time — see the derived `iglooWorkStaffAccessAvailable` write
      // below. Not a form field, no control renders it, nothing calls setFieldValue on it.
      iglooWorkStaffAccessAvailable,
      visionlineAvailable,
      iglooWorkStaffMasterKeyAvailable,
      twoNAvailable,
      verifyPhoneNumber,
      coolAutomationAvailable,
      ecobeeAvailable,
      honeywellAvailable,
      butterflyAvailable,
      bmxInAppCallsAvailable,
      butterflyVisitorAccessAvailable,
      aiWriterAvailable,
      flexibleRoleAvailable,
      fitnessAvailable,
      quadientWebhook,
      saltoSpaceDeviceBindingAvailable,
      thermostatsScheduleAvailable,
      parcelPendingAvailable,
      tags,
      payRentLink,
      maintenanceRequestLink,
      loyaltyAvailable,
      rentAvailable,
      amenityRewardsAvailable,
      maintenanceCallAvailable,
      inspectionsAvailable,
      maintenanceEscalationAvailable,
      reputationAvailable,
      eventTicketsAvailable,
      amenityPassesAvailable,
      chatCustom,
      chatCustomName,
      parkingManagementAvailable,
      visitorParkingAvailable,
      keylessAccessForStaffAvailable,
      amenityLocationAvailable,
      amenityMarketplaceAvailable,
      residentEventsAvailable,
      incidentReportsAvailable,
      assetTrackingAvailable,
      actionLogAvailable,
      frontDeskAvailable,
      restaurantManagerAvailable,
      customerPortalAvailable,
      accountPastDue,
      accountSuspended,
      qrCodeSurveysAvailable,
      appUserStatusAvailable,
      websiteWidgetsAvailable,
      displayBuildingNumber,
      eventConvenienceFeeAvailable,
      reservationConvenienceFeeAvailable,
    },
    { setSubmitting, setFieldValue },
  ) => {
    const newBuildingData = {
      active,
      name,
      address,
      securityDispatch,
      onsiteRetailLink,
      phone,
      code,
      hubspotDealId,
      residentConvenienceFeeRate,
      link,
      city,
      email,
      appName,
      keylessAccessAvailable,
      state,
      iosAppStoreLink,
      googlePlayLink,
      payRentLink,
      maintenanceRequestLink,
      emergencyPhone,
      buildingResidence,
      isHoa,
      avatarChangeRestricted,
      nameChangeRestricted,
      servicesAvailable,
      buildingEssentialsAvailable,
      perksAvailable,
      mealMeAvailable,
      groupsAvailable,
      tvAppAvailable,
      packageRoomAvailable,
      rewardsAvailable,
      doorActivityNotificationsAvailable,
      valetAvailable,
      marketShopAvailable,
      // Note: If market shop is not available, then market shop restaurant should not be available
      marketShopRestaurantAvailable: marketShopAvailable ? marketShopRestaurantAvailable : false,
      marketplacePostsReview,
      amenityReservations,
      amenitiesAcrAvailable,
      communityCalendar,
      timezone,
      guestAccess,
      physicalKeyAvailable,
      customAccessAvailable,
      maintenanceRequestAvailable,
      manualApproval,
      groupsPostReview,
      communicationAvailable,
      checklistsAvailable,
      chatAvailable,
      widgetChatAvailable,
      homepageAvailable,
      chatPM,
      chatFD,
      chatMT,
      packagesAvailable,
      packagesQuickScanAvailable,
      packagesD2DAvailable,
      communicateSMSAvailable,
      buildingAnalyticsAvailable,
      managementUsersAvailable,
      latchAvailable,
      brivoAvailable,
      openpathAvailable,
      dormakabaAvailable,
      quadientWebhook,
      saltoKSAvailable,
      saltoSpaceAvailable,
      saltoSpaceUserSyncAvailable:
        keylessAccessAvailable && saltoSpaceAvailable ? saltoSpaceUserSyncAvailable : false,
      saltoSpaceVisitorAccessAvailable:
        keylessAccessAvailable && saltoSpaceAvailable ? saltoSpaceVisitorAccessAvailable : false,
      saltoSpacePinVisitorAccessAvailable:
        keylessAccessAvailable && saltoSpaceAvailable ? saltoSpacePinVisitorAccessAvailable : false,
      saltoSpaceFobsAvailable:
        keylessAccessAvailable && saltoSpaceAvailable ? saltoSpaceFobsAvailable : false,
      saltoSpaceDeviceBindingAvailable:
        keylessAccessAvailable && saltoSpaceAvailable ? saltoSpaceDeviceBindingAvailable : false,
      brivoVisitorAccessAvailable:
        keylessAccessAvailable && brivoAvailable ? brivoVisitorAccessAvailable : false,
      brivoMagicButtonAvailable:
        keylessAccessAvailable && brivoAvailable ? brivoMagicButtonAvailable : false,
      swiftlaneVisitorAccessAvailable:
        keylessAccessAvailable && swiftlaneAvailable ? swiftlaneVisitorAccessAvailable : false,
      swiftlaneVoipAvailable:
        keylessAccessAvailable && swiftlaneAvailable ? swiftlaneVoipAvailable : false,
      swiftlaneAutoUserAvailable:
        keylessAccessAvailable && swiftlaneAvailable ? swiftlaneAutoUserAvailable : false,
      bmxInAppCallsAvailable:
        keylessAccessAvailable && butterflyAvailable ? bmxInAppCallsAvailable : false,
      butterflyVisitorAccessAvailable:
        keylessAccessAvailable && butterflyAvailable ? butterflyVisitorAccessAvailable : false,
      pointCentralThermostatsAvailable:
        keylessAccessAvailable && pointCentralAvailable ? pointCentralThermostatsAvailable : false,
      pointCentralLocksAvailable:
        keylessAccessAvailable && pointCentralAvailable ? pointCentralLocksAvailable : false,
      akuvoxAvailable,
      rentlyAvailable,
      swiftlaneAvailable,
      remoteLockAvailable,
      remoteLockFobAvailable:
        keylessAccessAvailable && remoteLockAvailable ? remoteLockFobAvailable : false,
      remoteLockVisitorAccessAvailable:
        keylessAccessAvailable && remoteLockAvailable ? remoteLockVisitorAccessAvailable : false,
      accentraAvailable,
      pointCentralAvailable,
      iglooWorkAvailable,
      iglooWorkVisitorAccessAvailable:
        keylessAccessAvailable && iglooWorkAvailable ? iglooWorkVisitorAccessAvailable : false,
      iglooWorkAlgoPinAvailable:
        keylessAccessAvailable && iglooWorkAvailable ? iglooWorkAlgoPinAvailable : false,
      iglooWorkCustomPinAvailable:
        keylessAccessAvailable && iglooWorkAvailable ? iglooWorkCustomPinAvailable : false,
      ...getIglooWorkStaffAccessAvailablePatch({
        iglooWorkAvailable,
        keylessAccessForStaffAvailable,
        serverIglooWorkStaffAccessAvailable: iglooWorkStaffAccessAvailable,
      }),
      visionlineAvailable,
      iglooWorkStaffMasterKeyAvailable: iglooWorkAvailable
        ? iglooWorkStaffMasterKeyAvailable
        : false,
      twoNAvailable,
      verifyPhoneNumber,
      coolAutomationAvailable,
      ecobeeAvailable,
      honeywellAvailable,
      butterflyAvailable,
      aiWriterAvailable,
      flexibleRoleAvailable,
      fitnessAvailable,
      thermostatsScheduleAvailable,
      loyaltyAvailable,
      rentAvailable,
      parcelPendingAvailable,
      amenityRewardsAvailable,
      maintenanceCallAvailable,
      inspectionsAvailable,
      maintenanceEscalationAvailable,
      reputationAvailable,
      eventTicketsAvailable,
      amenityPassesAvailable,
      chatCustom,
      chatCustomName: chatCustomName || 'Custom Inbox',
      tags: tags.map((tag) => tag.label),
      parkingManagementAvailable,
      visitorParkingAvailable,
      keylessAccessForStaffAvailable,
      // Derived, not a form field. Product decision (Kirill, 2026-07-27): staff keyless is ONE
      // generic toggle covering every integration, never a per-provider one — the same rule
      // EL-12922/EL-12923 apply to the guest flow. The backend keeps two distinct flags
      // (`keylessAccessForStaffAvailable` gates RemoteLock staff; `iglooWorkStaffAccessAvailable`
      // is the ONLY flag the Igloo staff endpoints check), so the single UI toggle has to write
      // both. Gated on `iglooWorkAvailable` for the same reason the visitor flag above is: a
      // property without the Igloo integration must never report Igloo staff access as on.
      //
      // Only sent when it actually differs from the server value read at load time — see
      // getIglooWorkStaffAccessAvailablePatch. KEYLESS_ACCESS_DEPENDENT_FIELDS below is the other
      // half: it force-syncs this flag when the whole keyless master switch goes off.
      ...getIglooWorkStaffAccessAvailablePatch({
        iglooWorkAvailable,
        keylessAccessForStaffAvailable,
        serverIglooWorkStaffAccessAvailable: iglooWorkStaffAccessAvailable,
      }),
      amenityLocationAvailable,
      amenityMarketplaceAvailable,
      residentEventsAvailable,
      incidentReportsAvailable,
      assetTrackingAvailable,
      actionLogAvailable,
      frontDeskAvailable,
      restaurantManagerAvailable,
      customerPortalAvailable,
      accountPastDue,
      accountSuspended,
      qrCodeSurveysAvailable,
      appUserStatusAvailable,
      websiteWidgetsAvailable,
      displayBuildingNumber,
      eventConvenienceFeeAvailable,
      reservationConvenienceFeeAvailable,
    }

    if (!newBuildingData.keylessAccessAvailable) {
      KEYLESS_ACCESS_DEPENDENT_FIELDS.forEach((field) => {
        newBuildingData[field] = false
      })
    }

    if (!newBuildingData.remoteLockAvailable) {
      newBuildingData.remoteLockFobAvailable = false
      newBuildingData.remoteLockVisitorAccessAvailable = false
    }

    const hasActiveThermostatIntegration =
      newBuildingData.ecobeeAvailable || newBuildingData.honeywellAvailable
    if (!hasActiveThermostatIntegration) {
      newBuildingData.thermostatsScheduleAvailable = false
    }

    if (logo.file) {
      newBuildingData.logo = logo.file
    }

    if (image.file) {
      newBuildingData.image = image.file
    }

    if (!isHoa) {
      newBuildingData.avatarChangeRestricted = false
    }

    if (isNew && country) {
      newBuildingData.country = country
    }

    // Legacy zips that predate the US/CA formats are rejected by the API, so an unchanged stored value is not re-sent
    // only new buildings or an edited zip go out.
    if (isNew || postalCode !== selectedBuilding.postalCode) {
      const formattedPostalCode =
        country === 'CA' && ZIP_CODE_CA_REGEX.test(postalCode)
          ? formatCanadianPostalCode(postalCode)
          : postalCode

      newBuildingData.postalCode = formattedPostalCode

      // The store value may be unchanged (e.g. "a5v0w6" -> "A5V 0W6"), in which case
      // enableReinitialize won't reset the form, so sync the formatted value back manually.
      setFieldValue('postalCode', formattedPostalCode, false)
    }

    setSubmitting(true)
    setFormError('')

    if (parcelPendingAvailable) {
      try {
        const { data } = await API.generateParcelPendingWebhook(buildingId)
        if (data === 'OK')
          displaySnackbar(t('organisation:places.building.toasts.parcelPendingEnabled'))
        else {
          newBuildingData.parcelPendingAvailable = false
          throw new Error(t('organisation:places.building.errors.parcelPendingFailed'))
        }
      } catch {
        displaySnackbar(t('organisation:places.building.errors.parcelPendingFailed'))
      }
    }

    const handleSubmitError = (err) => {
      setSubmitting(false)
      if (err?.errCode === 409 || err === 409) {
        setFormError(t('organisation:places.building.errors.codeExists'))
      } else {
        setFormError(err?.message || t('organisation:shared.serverError'))
      }
    }

    if (isNew) {
      createBuilding(newBuildingData)
        .then(() => {
          setSubmitting(false)
          displaySnackbar(t('organisation:places.building.toasts.created'))
          setFormError('')
        })
        .catch(handleSubmitError)
    } else {
      const updateRequest = updateBuilding(newBuildingData, id)
      customPinTracking.trackSave(updateRequest, newBuildingData)
      updateRequest
        .then(() => {
          setSubmitting(false)
          displaySnackbar(t('organisation:places.building.toasts.updated'))
        })
        .catch(handleSubmitError)
    }
  }

  const handleRegisterBuildingFoAccentraIntegration = async () => {
    setLoading(true)
    try {
      await API.registerBuildingForAccentra(buildingId, accentraCredentials)
      displaySnackbar(t('organisation:places.building.toasts.accentraRegistered'))
      setAccentraCredentials({ login: '', password: '' })
    } catch {
      displaySnackbar(t('organisation:places.building.errors.accentraRegistrationFailed'))
    } finally {
      setLoading(false)
    }
  }

  const displayErrors = (errors) => {
    if (!formError && Object.keys(errors).length === 0) return ''

    const fieldLabels = {
      name: t('organisation:places.building.fields.name.label'),
      address: t('organisation:places.building.fields.address.label'),
      timezone: t('organisation:places.building.timezone.label'),
      postalCode: t('organisation:places.building.fields.postalCode.label'),
      phone: t('organisation:places.building.fields.phone.label'),
      code: t('organisation:places.building.fields.code.label'),
      buildingResidence: t('organisation:places.building.fields.buildingResidence.label'),
      link: t('organisation:places.building.fields.link.label'),
      email: t('organisation:places.building.fields.email.label'),
      iosAppStoreLink: t('organisation:places.building.fields.iosAppStoreLink.label'),
      googlePlayLink: t('organisation:places.building.fields.googlePlayLink.label'),
      emergencyPhone: t('organisation:places.building.fields.emergencyPhone.label'),
    }

    return [
      formError,
      ...Object.entries(errors).map(([key, value]) => `${fieldLabels[key] ?? key}: ${value}`),
    ]
      .filter(Boolean)
      .join(', ')
  }

  const validationSchema = Yup.object().shape({
    link: formValidation.link,
    iosAppStoreLink: formValidation.link,
    googlePlayLink: formValidation.link,
    emergencyPhone: formValidation.phone.notRequired(),
    name: formValidation.required,
    address: formValidation.required,
    city: Yup.string().notRequired(),
    state: Yup.string().notRequired(),
    country: Yup.string().oneOf(['US', 'CA']).notRequired(),
    timezone: formValidation.timezone,
    securityDispatch: formValidation.phoneNotRequired,
    onsiteRetailLink: formValidation.link.notRequired(),
    code: formValidation.required,
    phone: formValidation.required,
    email: formValidation.email.notRequired(),
    appName: Yup.string().notRequired(),
    active: Yup.boolean(),
    keylessAccessAvailable: Yup.boolean(),
    postalCode: formValidation.postalCode(isNew ? undefined : selectedBuilding.postalCode),
    buildingResidence: Yup.number().required(t('common:validation.required')),
    isHoa: Yup.boolean(),
    avatarChangeRestricted: Yup.boolean(),
    nameChangeRestricted: Yup.boolean(),
    servicesAvailable: Yup.boolean(),
    buildingEssentialsAvailable: Yup.boolean(),
    perksAvailable: Yup.boolean(),
    mealMeAvailable: Yup.boolean(),
    groupsAvailable: Yup.boolean(),
    tvAppAvailable: Yup.boolean(),
    packageRoomAvailable: Yup.boolean(),
    rewardsAvailable: Yup.boolean(),
    doorActivityNotificationsAvailable: Yup.boolean(),
    valetAvailable: Yup.boolean(),
    marketShopAvailable: Yup.boolean(),
    marketShopRestaurantAvailable: Yup.boolean(),
    marketplacePostsReview: Yup.boolean(),
    amenityReservations: Yup.boolean(),
    amenitiesAcrAvailable: Yup.boolean(),
    communityCalendar: Yup.boolean(),
    guestAccess: Yup.boolean(),
    physicalKeyAvailable: Yup.boolean(),
    customAccessAvailable: Yup.boolean(),
    maintenanceRequestAvailable: Yup.boolean(),
    manualApproval: Yup.boolean(),
    groupsPostReview: Yup.boolean(),
    communicationAvailable: Yup.boolean(),
    checklistsAvailable: Yup.boolean(),
    chatAvailable: Yup.boolean(),
    widgetChatAvailable: Yup.boolean(),
    homepageAvailable: Yup.boolean(),
    chatPM: Yup.boolean(),
    chatFD: Yup.boolean(),
    chatMT: Yup.boolean(),
    packagesAvailable: Yup.boolean(),
    packagesQuickScanAvailable: Yup.boolean(),
    packagesD2DAvailable: Yup.boolean(),
    communicateSMSAvailable: Yup.boolean(),
    buildingAnalyticsAvailable: Yup.boolean(),
    managementUsersAvailable: Yup.boolean(),
    butterflyAvailable: Yup.boolean(),
    bmxInAppCallsAvailable: Yup.boolean(),
    butterflyVisitorAccessAvailable: Yup.boolean(),
    brivoAvailable: Yup.boolean(),
    openpathAvailable: Yup.boolean(),
    dormakabaAvailable: Yup.boolean(),
    latchAvailable: Yup.boolean(),
    saltoKSAvailable: Yup.boolean(),
    saltoSpaceAvailable: Yup.boolean(),
    saltoSpaceUserSyncAvailable: Yup.boolean(),
    saltoSpaceVisitorAccessAvailable: Yup.boolean(),
    saltoSpacePinVisitorAccessAvailable: Yup.boolean(),
    saltoSpaceFobsAvailable: Yup.boolean(),
    brivoVisitorAccessAvailable: Yup.boolean(),
    brivoMagicButtonAvailable: Yup.boolean(),
    akuvoxAvailable: Yup.boolean(),
    rentlyAvailable: Yup.boolean(),
    swiftlaneAvailable: Yup.boolean(),
    swiftlaneVisitorAccessAvailable: Yup.boolean(),
    swiftlaneVoipAvailable: Yup.boolean(),
    swiftlaneAutoUserAvailable: Yup.boolean(),
    remoteLockAvailable: Yup.boolean(),
    remoteLockFobAvailable: Yup.boolean(),
    remoteLockVisitorAccessAvailable: Yup.boolean(),
    accentraAvailable: Yup.boolean(),
    pointCentralAvailable: Yup.boolean(),
    pointCentralThermostatsAvailable: Yup.boolean(),
    pointCentralLocksAvailable: Yup.boolean(),
    iglooWorkAvailable: Yup.boolean(),
    visionlineAvailable: Yup.boolean(),
    iglooWorkVisitorAccessAvailable: Yup.boolean(),
    iglooWorkAlgoPinAvailable: Yup.boolean(),
    iglooWorkCustomPinAvailable: Yup.boolean(),
    iglooWorkStaffAccessAvailable: Yup.boolean(),
    iglooWorkStaffMasterKeyAvailable: Yup.boolean(),
    twoNAvailable: Yup.boolean(),
    verifyPhoneNumber: Yup.boolean(),
    coolAutomationAvailable: Yup.boolean(),
    ecobeeAvailable: Yup.boolean(),
    honeywellAvailable: Yup.boolean(),
    aiWriterAvailable: Yup.boolean(),
    flexibleRoleAvailable: Yup.boolean(),
    fitnessAvailable: Yup.boolean(),
    saltoSpaceDeviceBindingAvailable: Yup.boolean(),
    thermostatsScheduleAvailable: Yup.boolean(),
    tags: Yup.array().of(Yup.string()),
    payRentLink: Yup.string(),
    maintenanceRequestLink: Yup.string(),
    parcelPendingAvailable: Yup.boolean(),
    loyaltyAvailable: Yup.boolean(),
    rentAvailable: Yup.boolean(),
    amenityRewardsAvailable: Yup.boolean(),
    maintenanceCallAvailable: Yup.boolean(),
    inspectionsAvailable: Yup.boolean(),
    maintenanceEscalationAvailable: Yup.boolean(),
    reputationAvailable: Yup.boolean(),
    eventTicketsAvailable: Yup.boolean(),
    amenityPassesAvailable: Yup.boolean(),
    chatCustom: Yup.boolean(),
    chatCustomName: Yup.string().notRequired(),
    parkingManagementAvailable: Yup.boolean(),
    visitorParkingAvailable: Yup.boolean(),
    keylessAccessForStaffAvailable: Yup.boolean(),
    incidentReportsAvailable: Yup.boolean(),
    assetTrackingAvailable: Yup.boolean(),
    actionLogAvailable: Yup.boolean(),
    frontDeskAvailable: Yup.boolean(),
    restaurantManagerAvailable: Yup.boolean(),
    customerPortalAvailable: Yup.boolean(),
    quadientWebhook: Yup.string(),
    amenityLocationAvailable: Yup.boolean(),
    amenityMarketplaceAvailable: Yup.boolean(),
    residentEventsAvailable: Yup.boolean(),
    accountPastDue: Yup.boolean(),
    accountSuspended: Yup.boolean(),
    qrCodeSurveysAvailable: Yup.boolean(),
    appUserStatusAvailable: Yup.boolean(),
    websiteWidgetsAvailable: Yup.boolean(),
    displayBuildingNumber: Yup.boolean(),
  })

  const buildingTags = [...new Set((buildings || []).flatMap((building) => building.tags))]
  const isActuallyNew =
    typeof selectedBuilding !== 'undefined' && typeof selectedBuilding.id === 'undefined'

  const handleCopyParcelPendingCallbackURL = async () => {
    try {
      await navigator.clipboard.writeText(selectedBuilding.quadientWebhook)
      displaySnackbar(t('organisation:places.building.toasts.callbackUrlCopied'))
    } catch {
      console.error('Error on copying the callback URL')
    }
  }

  const handleSyncSaltoSpace = async () => {
    setIsSyncingSaltoSpace(true)
    try {
      await API.syncSaltoSpaceAccessPoints(buildingId)
      displaySnackbar('Salto Space data synchronized successfully.')
    } catch {
      displaySnackbar('Synchronization failed. Please try again later.', true)
    } finally {
      setIsSyncingSaltoSpace(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        image: {
          file: false,
          image: getAssetLink({ path: 'chitown-building', file: selectedBuilding.image }) || null,
        },
        active: selectedBuilding.active || false,
        name: selectedBuilding.name || '',
        link: selectedBuilding.link || '',
        city: selectedBuilding.city || '',
        state: selectedBuilding.state || '',
        country: selectedBuilding.country || 'US',
        email: selectedBuilding.email || '',
        appName: selectedBuilding.appName || '',
        keylessAccessAvailable: selectedBuilding.keylessAccessAvailable || false,
        iosAppStoreLink: selectedBuilding.iosAppStoreLink || '',
        googlePlayLink: selectedBuilding.googlePlayLink || '',
        emergencyPhone: selectedBuilding.emergencyPhone || '',
        address: selectedBuilding.address || '',
        timezone: selectedBuilding.timezone || 'America/Chicago',
        securityDispatch: selectedBuilding.securityDispatch || '',
        onsiteRetailLink: selectedBuilding.onsiteRetailLink || '',
        postalCode: selectedBuilding.postalCode || '',
        phone: selectedBuilding.phone || '',
        code: selectedBuilding.code || '',
        hubspotDealId: selectedBuilding.hubspotDealId || '',
        residentConvenienceFeeRate: selectedBuilding.residentConvenienceFeeRate || '',
        buildingResidence: selectedBuilding.buildingResidence || '',
        id: selectedBuilding.id || '',
        logo: {
          file: false,
          image: getAssetLink({ path: 'chitown-building', file: selectedBuilding.logo }) || null,
        },
        payRentLink: selectedBuilding.payRentLink || '',
        maintenanceRequestLink: selectedBuilding.maintenanceRequestLink || '',
        isHoa: selectedBuilding.isHoa || false,
        avatarChangeRestricted:
          (selectedBuilding.isHoa && selectedBuilding.avatarChangeRestricted) || false,
        nameChangeRestricted: selectedBuilding.nameChangeRestricted || false,
        servicesAvailable: selectedBuilding.servicesAvailable || false,
        buildingEssentialsAvailable: selectedBuilding.buildingEssentialsAvailable || false,
        perksAvailable: selectedBuilding.perksAvailable || false,
        mealMeAvailable: selectedBuilding.mealMeAvailable || false,
        groupsAvailable: selectedBuilding.groupsAvailable || false,
        tvAppAvailable: selectedBuilding.tvAppAvailable || false,
        packageRoomAvailable: selectedBuilding.packageRoomAvailable || false,
        rewardsAvailable: selectedBuilding.rewardsAvailable || false,
        doorActivityNotificationsAvailable:
          selectedBuilding.doorActivityNotificationsAvailable || false,
        valetAvailable: selectedBuilding.valetAvailable || false,
        marketShopAvailable: selectedBuilding.marketShopAvailable || false,
        marketShopRestaurantAvailable: selectedBuilding.marketShopRestaurantAvailable || false,
        marketplacePostsReview: selectedBuilding.marketplacePostsReview || false,
        amenityReservations: selectedBuilding.amenityReservations || false,
        amenitiesAcrAvailable: selectedBuilding.amenitiesAcrAvailable || false,
        communityCalendar: selectedBuilding.communityCalendar || false,
        guestAccess: selectedBuilding.guestAccess || false,
        physicalKeyAvailable: selectedBuilding.physicalKeyAvailable || false,
        customAccessAvailable: selectedBuilding.customAccessAvailable || false,
        maintenanceRequestAvailable: selectedBuilding.maintenanceRequestAvailable || false,
        manualApproval: selectedBuilding.manualApproval || false,
        groupsPostReview: selectedBuilding.groupsPostReview || false,
        communicationAvailable: selectedBuilding.communicationAvailable || false,
        checklistsAvailable: selectedBuilding.checklistsAvailable || false,
        chatAvailable: selectedBuilding.chatAvailable || false,
        widgetChatAvailable: selectedBuilding.widgetChatAvailable || false,
        homepageAvailable: selectedBuilding.homepageAvailable || false,
        chatPM: selectedBuilding.chatPM || false,
        chatFD: selectedBuilding.chatFD || false,
        chatMT: selectedBuilding.chatMT || false,
        packagesAvailable: selectedBuilding.packagesAvailable || false,
        packagesQuickScanAvailable: selectedBuilding.packagesQuickScanAvailable || false,
        packagesD2DAvailable: selectedBuilding.packagesD2DAvailable || false,
        communicateSMSAvailable: selectedBuilding.communicateSMSAvailable || false,
        buildingAnalyticsAvailable: selectedBuilding.buildingAnalyticsAvailable || false,
        managementUsersAvailable: selectedBuilding.managementUsersAvailable || false,
        brivoAvailable: selectedBuilding.brivoAvailable || false,
        openpathAvailable: selectedBuilding.openpathAvailable || false,
        dormakabaAvailable: selectedBuilding.dormakabaAvailable || false,
        butterflyAvailable: selectedBuilding.butterflyAvailable || false,
        bmxInAppCallsAvailable: selectedBuilding.bmxInAppCallsAvailable || false,
        butterflyVisitorAccessAvailable: selectedBuilding.butterflyVisitorAccessAvailable || false,
        latchAvailable: selectedBuilding.latchAvailable || false,
        saltoKSAvailable: selectedBuilding.saltoKSAvailable || false,
        saltoSpaceAvailable: selectedBuilding.saltoSpaceAvailable || false,
        saltoSpaceUserSyncAvailable: selectedBuilding.saltoSpaceUserSyncAvailable || false,
        saltoSpaceVisitorAccessAvailable:
          selectedBuilding.saltoSpaceVisitorAccessAvailable || false,
        saltoSpacePinVisitorAccessAvailable:
          selectedBuilding.saltoSpacePinVisitorAccessAvailable || false,
        saltoSpaceFobsAvailable: selectedBuilding.saltoSpaceFobsAvailable || false,
        brivoVisitorAccessAvailable: selectedBuilding.brivoVisitorAccessAvailable || false,
        brivoMagicButtonAvailable: selectedBuilding.brivoMagicButtonAvailable || false,
        akuvoxAvailable: selectedBuilding.akuvoxAvailable || false,
        rentlyAvailable: selectedBuilding.rentlyAvailable || false,
        swiftlaneAvailable: selectedBuilding.swiftlaneAvailable || false,
        swiftlaneVisitorAccessAvailable: selectedBuilding.swiftlaneVisitorAccessAvailable || false,
        swiftlaneVoipAvailable: selectedBuilding.swiftlaneVoipAvailable || false,
        swiftlaneAutoUserAvailable: selectedBuilding.swiftlaneAutoUserAvailable || false,
        remoteLockAvailable: selectedBuilding.remoteLockAvailable || false,
        remoteLockFobAvailable: selectedBuilding.remoteLockFobAvailable || false,
        remoteLockVisitorAccessAvailable:
          selectedBuilding.remoteLockVisitorAccessAvailable || false,
        accentraAvailable: selectedBuilding.accentraAvailable || false,
        pointCentralAvailable: selectedBuilding.pointCentralAvailable || false,
        pointCentralThermostatsAvailable:
          selectedBuilding.pointCentralThermostatsAvailable || false,
        pointCentralLocksAvailable: selectedBuilding.pointCentralLocksAvailable || false,
        iglooWorkAvailable: selectedBuilding.iglooWorkAvailable || false,
        iglooWorkVisitorAccessAvailable: selectedBuilding.iglooWorkVisitorAccessAvailable || false,
        iglooWorkAlgoPinAvailable: selectedBuilding.iglooWorkAlgoPinAvailable || false,
        iglooWorkCustomPinAvailable: selectedBuilding.iglooWorkCustomPinAvailable || false,
        // Not an editable field — no control renders it. Read only so the submit handler can
        // diff the re-derived value against server truth before deciding to send it.
        iglooWorkStaffAccessAvailable: selectedBuilding.iglooWorkStaffAccessAvailable || false,
        visionlineAvailable: selectedBuilding.visionlineAvailable || false,
        iglooWorkStaffMasterKeyAvailable:
          selectedBuilding.iglooWorkStaffMasterKeyAvailable || false,
        twoNAvailable: selectedBuilding.twoNAvailable || false,
        verifyPhoneNumber: selectedBuilding.verifyPhoneNumber || false,
        coolAutomationAvailable: selectedBuilding.coolAutomationAvailable || false,
        ecobeeAvailable: selectedBuilding.ecobeeAvailable || false,
        honeywellAvailable: selectedBuilding.honeywellAvailable || false,
        aiWriterAvailable: selectedBuilding.aiWriterAvailable || false,
        flexibleRoleAvailable: selectedBuilding.flexibleRoleAvailable || false,
        parcelPendingAvailable: selectedBuilding.parcelPendingAvailable || false,
        fitnessAvailable: selectedBuilding.fitnessAvailable || false,
        saltoSpaceDeviceBindingAvailable:
          selectedBuilding.saltoSpaceDeviceBindingAvailable || false,
        thermostatsScheduleAvailable: selectedBuilding.thermostatsScheduleAvailable || false,
        loyaltyAvailable: selectedBuilding.loyaltyAvailable || false,
        rentAvailable: selectedBuilding.rentAvailable || false,
        amenityRewardsAvailable: selectedBuilding.amenityRewardsAvailable || false,
        maintenanceCallAvailable: selectedBuilding.maintenanceCallAvailable || false,
        inspectionsAvailable: selectedBuilding.inspectionsAvailable || false,
        maintenanceEscalationAvailable: selectedBuilding.maintenanceEscalationAvailable || false,
        reputationAvailable: selectedBuilding.reputationAvailable || false,
        eventTicketsAvailable: selectedBuilding.eventTicketsAvailable || false,
        amenityPassesAvailable: selectedBuilding.amenityPassesAvailable || false,
        chatCustom: selectedBuilding.chatCustom || false,
        chatCustomName: selectedBuilding.chatCustomName || '',
        tags: (selectedBuilding.tags || []).map((tag) => ({ value: tag, label: tag })),
        parkingManagementAvailable: selectedBuilding.parkingManagementAvailable || false,
        visitorParkingAvailable: selectedBuilding.visitorParkingAvailable || false,
        keylessAccessForStaffAvailable: selectedBuilding.keylessAccessForStaffAvailable || false,
        apartmentsId: selectedBuilding.apartmentsId || '',
        quadientWebhook: selectedBuilding.quadientWebhook,
        amenityLocationAvailable: selectedBuilding.amenityLocationAvailable || false,
        amenityMarketplaceAvailable: selectedBuilding.amenityMarketplaceAvailable || false,
        residentEventsAvailable: selectedBuilding.residentEventsAvailable || false,
        incidentReportsAvailable: selectedBuilding.incidentReportsAvailable || false,
        assetTrackingAvailable: selectedBuilding.assetTrackingAvailable || false,
        actionLogAvailable: selectedBuilding.actionLogAvailable || false,
        frontDeskAvailable: selectedBuilding.frontDeskAvailable || false,
        restaurantManagerAvailable: selectedBuilding.restaurantManagerAvailable || false,
        customerPortalAvailable: selectedBuilding.customerPortalAvailable || false,
        accountPastDue: selectedBuilding.accountPastDue || false,
        accountSuspended: selectedBuilding.accountSuspended || false,
        qrCodeSurveysAvailable: selectedBuilding.qrCodeSurveysAvailable || false,
        appUserStatusAvailable: selectedBuilding.appUserStatusAvailable || false,
        websiteWidgetsAvailable: selectedBuilding.websiteWidgetsAvailable || false,
        displayBuildingNumber: selectedBuilding.displayBuildingNumber || false,
        eventConvenienceFeeAvailable: selectedBuilding.eventConvenienceFeeAvailable || false,
        reservationConvenienceFeeAvailable:
          selectedBuilding.reservationConvenienceFeeAvailable || false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      ref={formRef}
    >
      {({
        handleChange,
        setFieldValue,
        handleSubmit,
        values,
        errors,
        touched,
        setFieldTouched,
        isSubmitting,
        isValid,
        dirty,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <div className={`ctp-building ${isActuallyNew ? 'ctp-building--new' : ''}`}>
              <div className="ctp-building__header">
                <h2>
                  {isActuallyNew
                    ? t('organisation:places.building.titleNew')
                    : t('organisation:places.building.titleEdit')}
                </h2>
              </div>
              <div className="form form--object-profile">
                <div className="columns">
                  <div className="column">
                    <ImageField
                      name={'logo'}
                      placeholderImage={getAssetLink({
                        path: 'chitown-building',
                        file: 'building_logo.png',
                      })}
                      additionalClass={'field-image--building-logo'}
                      label={t('organisation:places.building.logotype')}
                      imageRatio={16 / 9}
                      value={values.logo}
                      onChange={handleChange}
                    />
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.active')}
                          id="active"
                          name="active"
                          onChange={handleChange}
                          value={values.active}
                        />
                      </div>
                    </div>
                    <h3>{t('organisation:places.building.sections.accountStatus')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.accountPastDue')}
                          id="accountPastDue"
                          name="accountPastDue"
                          onChange={handleChange}
                          value={values.accountPastDue}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.accountSuspended')}
                          id="accountSuspended"
                          name="accountSuspended"
                          onChange={handleChange}
                          value={values.accountSuspended}
                        />
                      </div>
                    </div>
                    <TextField
                      name={'name'}
                      label={t('organisation:places.building.fields.name.label')}
                      placeholder={t('organisation:places.building.fields.name.placeholder')}
                      onChange={handleChange}
                      value={values.name}
                      isError={touched.name && !!errors.name}
                      onBlur={() => setFieldTouched('name')}
                    />
                    <TextField
                      name={'address'}
                      label={t('organisation:places.building.fields.address.label')}
                      placeholder={t('organisation:places.building.fields.address.placeholder')}
                      onChange={handleChange}
                      value={values.address}
                      isError={touched.address && !!errors.address}
                      onBlur={() => setFieldTouched('address')}
                    />
                    <SelectField
                      disabled={!!selectedBuilding.timezone}
                      additionalClass="ctp-building__select"
                      name="timezone"
                      options={[
                        {
                          value: TIMEZONES.PT,
                          label: t('organisation:places.building.timezone.options.pt'),
                        },
                        {
                          value: TIMEZONES.MT,
                          label: t('organisation:places.building.timezone.options.mt'),
                        },
                        {
                          value: TIMEZONES.AZ,
                          label: t('organisation:places.building.timezone.options.az'),
                        },
                        {
                          value: TIMEZONES.CT,
                          label: t('organisation:places.building.timezone.options.ct'),
                        },
                        {
                          value: TIMEZONES.ET,
                          label: t('organisation:places.building.timezone.options.et'),
                        },
                      ]}
                      label={t('organisation:places.building.timezone.label')}
                      placeholder={t('organisation:places.building.timezone.placeholder')}
                      onChange={handleChange}
                      value={values.timezone}
                      isError={touched.timezone && !!errors.timezone}
                      onBlur={() => setFieldTouched('timezone')}
                    />
                    <div className="columns">
                      <div className="column">
                        <TextField
                          name={'postalCode'}
                          label={t('organisation:places.building.fields.postalCode.label')}
                          placeholder={t(
                            'organisation:places.building.fields.postalCode.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.postalCode}
                          isError={touched.postalCode && !!errors.postalCode}
                          onBlur={() => setFieldTouched('postalCode')}
                        />
                      </div>
                      <div className="column">
                        <PhoneField
                          name={'phone'}
                          label={t('organisation:places.building.fields.phone.label')}
                          placeholder={t('organisation:places.building.fields.phone.placeholder')}
                          onChange={handleChange}
                          value={values.phone}
                          isError={touched.phone && !!errors.phone}
                          onBlur={() => setFieldTouched('postalCode')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column">
                        <TextField
                          name={'city'}
                          label={t('organisation:places.building.fields.city.label')}
                          placeholder={t('organisation:places.building.fields.city.placeholder')}
                          onChange={handleChange}
                          value={values.city}
                          isError={touched.city && !!errors.city}
                          onBlur={() => setFieldTouched('city')}
                        />
                      </div>
                      <div className="column">
                        <TextField
                          name={'state'}
                          label={t('organisation:places.building.fields.state.label')}
                          placeholder={t('organisation:places.building.fields.state.placeholder')}
                          onChange={handleChange}
                          value={values.state}
                          isError={touched.state && !!errors.state}
                          onBlur={() => setFieldTouched('state')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-6">
                        {isNew ? (
                          <SelectField
                            additionalClass="ctp-building__select"
                            name="country"
                            options={[
                              { value: 'US', label: 'United States (USD)' },
                              { value: 'CA', label: 'Canada (CAD)' },
                            ]}
                            label="Country"
                            placeholder="Select country"
                            onChange={handleChange}
                            value={values.country}
                            isError={touched.country && !!errors.country}
                            onBlur={() => setFieldTouched('country')}
                          />
                        ) : (
                          <TextField
                            name={'countryDisplay'}
                            label="Country"
                            value={values.country === 'CA' ? 'Canada' : 'United States'}
                            onChange={() => {}}
                            disabled
                          />
                        )}
                      </div>
                      <div className="column is-6">
                        <TextField
                          name={'currencyDisplay'}
                          label="Currency"
                          value={values.country === 'CA' ? 'CAD' : 'USD'}
                          onChange={() => {}}
                          disabled
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-6">
                        <TextField
                          name={'code'}
                          label={t('organisation:places.building.fields.code.label')}
                          leftIcon={<Icon icon="building" />}
                          placeholder={t('organisation:places.building.fields.code.placeholder')}
                          onChange={handleChange}
                          value={values.code}
                          isError={touched.code && !!errors.code}
                          onBlur={() => setFieldTouched('postalCode')}
                        />
                      </div>
                      <div className="column is-6">
                        <TextField
                          name={'buildingResidence'}
                          label={t('organisation:places.building.fields.buildingResidence.label')}
                          onChange={handleChange}
                          value={values.buildingResidence}
                          isError={touched.buildingResidence && !!errors.buildingResidence}
                          onBlur={() => setFieldTouched('buildingResidence')}
                        />
                      </div>
                    </div>

                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'link'}
                          label={t('organisation:places.building.fields.link.label')}
                          placeholder={t('organisation:places.building.fields.link.placeholder')}
                          onChange={handleChange}
                          value={values.link}
                          isError={touched.link && !!errors.link}
                          onBlur={() => setFieldTouched('link')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'email'}
                          label={t('organisation:places.building.fields.email.label')}
                          placeholder={t('organisation:places.building.fields.email.placeholder')}
                          onChange={handleChange}
                          value={values.email}
                          isError={touched.email && !!errors.email}
                          onBlur={() => setFieldTouched('email')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'appName'}
                          label={t('organisation:places.building.fields.appName.label')}
                          placeholder={t('organisation:places.building.fields.appName.placeholder')}
                          onChange={handleChange}
                          value={values.appName}
                          isError={touched.appName && !!errors.appName}
                          onBlur={() => setFieldTouched('appName')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-6">
                        <TextField
                          name={'iosAppStoreLink'}
                          label={t('organisation:places.building.fields.iosAppStoreLink.label')}
                          placeholder={t(
                            'organisation:places.building.fields.iosAppStoreLink.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.iosAppStoreLink}
                          isError={touched.iosAppStoreLink && !!errors.iosAppStoreLink}
                          onBlur={() => setFieldTouched('iosAppStoreLink')}
                        />
                      </div>
                      <div className="column is-6">
                        <TextField
                          name={'googlePlayLink'}
                          label={t('organisation:places.building.fields.googlePlayLink.label')}
                          placeholder={t(
                            'organisation:places.building.fields.googlePlayLink.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.googlePlayLink}
                          isError={touched.googlePlayLink && !!errors.googlePlayLink}
                          onBlur={() => setFieldTouched('googlePlayLink')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'payRentLink'}
                          label={t('organisation:places.building.fields.payRentLink.label')}
                          placeholder={t(
                            'organisation:places.building.fields.payRentLink.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.payRentLink}
                          isError={touched.payRentLink && !!errors.payRentLink}
                          onBlur={() => setFieldTouched('payRentLink')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'maintenanceRequestLink'}
                          label={t(
                            'organisation:places.building.fields.maintenanceRequestLink.label',
                          )}
                          placeholder={t(
                            'organisation:places.building.fields.maintenanceRequestLink.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.maintenanceRequestLink}
                          isError={
                            touched.maintenanceRequestLink && !!errors.maintenanceRequestLink
                          }
                          onBlur={() => setFieldTouched('maintenanceRequestLink')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'emergencyPhone'}
                          label={t('organisation:places.building.fields.emergencyPhone.label')}
                          placeholder={t(
                            'organisation:places.building.fields.emergencyPhone.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.emergencyPhone}
                          isError={touched.emergencyPhone && !!errors.emergencyPhone}
                          onBlur={() => setFieldTouched('emergencyPhone')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'chatCustomName'}
                          label={t('organisation:places.building.fields.chatCustomName.label')}
                          placeholder={t(
                            'organisation:places.building.fields.chatCustomName.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.chatCustomName}
                          isError={touched.chatCustomName && !!errors.chatCustomName}
                          onBlur={() => setFieldTouched('chatCustomName')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <FormFieldTags
                          name={'tags'}
                          options={buildingTags.map((tag) => ({ value: tag, label: tag }))}
                          label={t('organisation:places.building.fields.tags.label')}
                          placeholder={t('organisation:places.building.fields.tags.placeholder')}
                          onChange={setFieldValue}
                          value={values.tags}
                          isError={touched.tags && !!errors.tags}
                          onBlur={() => setFieldTouched('tags')}
                        />
                      </div>
                    </div>
                    {!isNew && (
                      <div className="columns">
                        <div className="column is-12">
                          <LockerSelect
                            label={t('organisation:places.building.luxerIds.label')}
                            placeholder={t('organisation:places.building.luxerIds.placeholder')}
                            building={selectedBuilding}
                          />
                        </div>
                      </div>
                    )}
                    <div className="ctp-building__copy-url-row">
                      <div className="ctp-building__copy-input-wrapper">
                        <label className="label">
                          {t('organisation:places.building.parcelPending.label')}
                        </label>

                        <input
                          readOnly
                          type="text"
                          tabIndex="-1"
                          className="input"
                          name="quadientWebhook"
                          placeholder={t('organisation:places.building.parcelPending.placeholder')}
                          value={
                            values.quadientWebhook ||
                            t('organisation:places.building.parcelPending.noCallbackUrl')
                          }
                          label={t('organisation:places.building.parcelPending.label')}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={!values.quadientWebhook}
                        onClick={handleCopyParcelPendingCallbackURL}
                        className="button button-primary auto-width"
                      >
                        {t('organisation:places.building.parcelPending.copyButton')}
                      </button>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <ApartmentsIdField
                          selectedBuilding={selectedBuilding}
                          updateBuildingApartmentsId={updateBuildingApartmentsId}
                          displaySnackbar={displaySnackbar}
                          handleChange={handleChange}
                          values={values}
                          touched={touched}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <TextField
                          name={'hubspotDealId'}
                          label={t('organisation:places.building.fields.hubspotDealId.label')}
                          placeholder={t(
                            'organisation:places.building.fields.hubspotDealId.placeholder',
                          )}
                          onChange={handleChange}
                          value={values.hubspotDealId}
                          isError={touched.hubspotDealId && !!errors.hubspotDealId}
                          onBlur={() => setFieldTouched('hubspotDealId')}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-12">
                        <ConvenienenceFeeField
                          handleChange={handleChange}
                          value={values.residentConvenienceFeeRate}
                          isError={
                            touched.residentConvenienceFeeRate &&
                            !!errors.residentConvenienceFeeRate
                          }
                          onBlur={() => setFieldTouched('residentConvenienceFeeRate')}
                        />
                      </div>
                    </div>
                    {values.accentraAvailable && (
                      <div className="ctp-building__column-form">
                        <TextField
                          label="Accentra Access Setup"
                          placeholder="Login"
                          onChange={(e) =>
                            setAccentraCredentials((prev) => ({
                              ...prev,
                              login: e.target.value,
                            }))
                          }
                          value={accentraCredentials.login}
                        />
                        <TextField
                          placeholder="Password"
                          type="password"
                          onChange={(e) =>
                            setAccentraCredentials((prev) => ({
                              ...prev,
                              password: e.target.value,
                            }))
                          }
                          value={accentraCredentials.password}
                        />
                        <button
                          type="button"
                          disabled={
                            isLoading || !accentraCredentials.login || !accentraCredentials.password
                          }
                          onClick={handleRegisterBuildingFoAccentraIntegration}
                          className={`button button-primary auto-width ${
                            isLoading ? 'is-loading' : ''
                          }`}
                        >
                          {t('organisation:places.building.accentraSetup.registerButton')}
                        </button>
                      </div>
                    )}

                    {values.saltoSpaceAvailable && !isNew && (
                      <div className="columns">
                        <div className="column is-12">
                          <label className="label">
                            {t('organisation:places.building.saltoSpaceCache.label')}
                          </label>
                          <button
                            type="button"
                            className={`button button-primary ${isSyncingSaltoSpace ? 'is-loading' : ''}`}
                            disabled={isSyncingSaltoSpace}
                            onClick={handleSyncSaltoSpace}
                          >
                            {t('organisation:places.building.saltoSpaceCache.syncButton')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="column">
                    <ImageField
                      name={'image'}
                      placeholderImage={getAssetLink({
                        path: 'chitown-building',
                        file: 'building_image.png',
                      })}
                      additionalClass={'field-image--big'}
                      label={t('organisation:places.building.buildingPhoto')}
                      imageRatio={16 / 9}
                      value={values.image}
                      onChange={handleChange}
                    />
                    <br />
                    <h3>{t('organisation:places.building.sections.forHoa')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.isHoa')}
                          id="isHoa"
                          name="isHoa"
                          onChange={() => {
                            if (values.isHoa) {
                              setFieldValue('isHoa', false)
                              setFieldValue('avatarChangeRestricted', false)
                            } else {
                              setFieldValue('isHoa', true)
                            }
                          }}
                          value={values.isHoa}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.avatarChangeRestricted')}
                          id="avatarChangeRestricted"
                          name="avatarChangeRestricted"
                          onChange={handleChange}
                          value={values.avatarChangeRestricted}
                          disabled={!values.isHoa}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.amenityPassesAvailable')}
                          id="amenityPassesAvailable"
                          name="amenityPassesAvailable"
                          onChange={handleChange}
                          value={values.amenityPassesAvailable}
                        />
                      </div>
                    </div>
                    <h3>{t('organisation:places.building.sections.forResidents')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.nameChangeRestricted')}
                          id="nameChangeRestricted"
                          name="nameChangeRestricted"
                          onChange={handleChange}
                          value={values.nameChangeRestricted}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.servicesAvailable')}
                          id="servicesAvailable"
                          name="servicesAvailable"
                          onChange={handleChange}
                          value={values.servicesAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.buildingEssentialsAvailable',
                          )}
                          id="buildingEssentialsAvailable"
                          name="buildingEssentialsAvailable"
                          onChange={handleChange}
                          value={values.buildingEssentialsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.perksAvailable')}
                          id="perksAvailable"
                          name="perksAvailable"
                          onChange={handleChange}
                          value={values.perksAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.mealMeAvailable')}
                          id="mealMeAvailable"
                          name="mealMeAvailable"
                          onChange={handleChange}
                          value={values.mealMeAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.verifyPhoneNumber')}
                          name="verifyPhoneNumber"
                          onChange={handleChange}
                          value={values.verifyPhoneNumber}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.fitnessAvailable')}
                          id="fitnessAvailable"
                          name="fitnessAvailable"
                          onChange={handleChange}
                          value={values.fitnessAvailable}
                        />
                      </div>
                    </div>
                    <br />
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.keylessAccessAvailable')}
                          name="keylessAccessAvailable"
                          onChange={(e) => {
                            handleChange(e)
                            if (!e.target.checked) {
                              KEYLESS_ACCESS_DEPENDENT_FIELDS.forEach((field) =>
                                setFieldValue(field, false),
                              )
                            }
                          }}
                          value={values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.parcelPendingAvailable')}
                          id="parcelPendingAvailable"
                          name="parcelPendingAvailable"
                          onChange={handleChange}
                          value={values.parcelPendingAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        {/* This one toggle governs staff keyless for every integration, Igloo
                            included — see the derived `iglooWorkStaffAccessAvailable` in the
                            submit payload. On an Igloo property both directions are destructive
                            and irreversible, so they are confirmed; on a non-Igloo property the
                            toggle behaves exactly as before and shows no popup. */}
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.keylessAccessForStaffAvailable',
                          )}
                          name="keylessAccessForStaffAvailable"
                          onChange={(event) => {
                            if (values.iglooWorkAvailable) {
                              setPendingKeylessStaffAccess(!values.keylessAccessForStaffAvailable)
                            } else {
                              handleChange(event)
                            }
                          }}
                          value={
                            values.keylessAccessAvailable
                              ? values.keylessAccessForStaffAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.butterflyAvailable')}
                          name="butterflyAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.butterflyAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.bmxInAppCallsAvailable')}
                          name="bmxInAppCallsAvailable"
                          onChange={() => {
                            if (!values.bmxInAppCallsAvailable && !values.butterflyAvailable)
                              setFieldValue('butterflyAvailable', true)
                            setFieldValue('bmxInAppCallsAvailable', !values.bmxInAppCallsAvailable)
                          }}
                          value={values.butterflyAvailable ? values.bmxInAppCallsAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.butterflyVisitorAccessAvailable',
                          )}
                          name="butterflyVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.butterflyAvailable
                              ? values.butterflyVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.butterflyAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.latchAvailable')}
                          name="latchAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.latchAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.brivoAvailable')}
                          name="brivoAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.brivoAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.brivoVisitorAccessAvailable',
                          )}
                          name="brivoVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.brivoAvailable
                              ? values.brivoVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.brivoAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.brivoMagicButtonAvailable',
                          )}
                          name="brivoMagicButtonAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.brivoAvailable
                              ? values.brivoMagicButtonAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.brivoAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.openpathAvailable')}
                          name="openpathAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.openpathAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.twoNAvailable')}
                          name="twoNAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.twoNAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.dormakabaAvailable')}
                          name="dormakabaAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.dormakabaAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.saltoKSAvailable')}
                          name="saltoKSAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.saltoKSAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.saltoSpaceAvailable')}
                          name="saltoSpaceAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.saltoSpaceAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.saltoSpaceUserSyncAvailable',
                          )}
                          name="saltoSpaceUserSyncAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.saltoSpaceAvailable
                              ? values.saltoSpaceUserSyncAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.saltoSpaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.saltoSpaceVisitorAccessAvailable',
                          )}
                          name="saltoSpaceVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.saltoSpaceAvailable
                              ? values.saltoSpaceVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.saltoSpaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.saltoSpaceFobsAvailable')}
                          name="saltoSpaceFobsAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.saltoSpaceAvailable
                              ? values.saltoSpaceFobsAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.saltoSpaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.saltoSpacePinVisitorAccessAvailable',
                          )}
                          name="saltoSpacePinVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.saltoSpaceAvailable
                              ? values.saltoSpacePinVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.saltoSpaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.saltoSpaceDeviceBindingAvailable',
                          )}
                          id="saltoSpaceDeviceBindingAvailable"
                          name="saltoSpaceDeviceBindingAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.saltoSpaceAvailable
                              ? values.saltoSpaceDeviceBindingAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.saltoSpaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.thermostatsScheduleAvailable',
                          )}
                          id="thermostatsScheduleAvailable"
                          name="thermostatsScheduleAvailable"
                          onChange={() => {
                            const hasActiveThermostatIntegration =
                              values.ecobeeAvailable || values.honeywellAvailable
                            if (
                              !values.thermostatsScheduleAvailable &&
                              !hasActiveThermostatIntegration
                            ) {
                              displaySnackbar(
                                t(
                                  'organisation:places.building.errors.hvacSchedulingRequiresThermostat',
                                ),
                                true,
                              )
                              return
                            }
                            setFieldValue(
                              'thermostatsScheduleAvailable',
                              !values.thermostatsScheduleAvailable,
                            )
                          }}
                          value={values.thermostatsScheduleAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.swiftlaneAvailable')}
                          name="swiftlaneAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.swiftlaneAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.swiftlaneVisitorAccessAvailable',
                          )}
                          name="swiftlaneVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.swiftlaneAvailable
                              ? values.swiftlaneVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.swiftlaneAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.swiftlaneVoipAvailable')}
                          name="swiftlaneVoipAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.swiftlaneAvailable
                              ? values.swiftlaneVoipAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.swiftlaneAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.swiftlaneAutoUserAvailable',
                          )}
                          name="swiftlaneAutoUserAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.swiftlaneAvailable
                              ? values.swiftlaneAutoUserAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.swiftlaneAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.akuvoxAvailable')}
                          name="akuvoxAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.akuvoxAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.rentlyAvailable')}
                          name="rentlyAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.rentlyAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.remoteLockAvailable')}
                          name="remoteLockAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.remoteLockAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.remoteLockFobAvailable')}
                          name="remoteLockFobAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.remoteLockAvailable
                              ? values.remoteLockFobAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.remoteLockAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.remoteLockVisitorAccessAvailable',
                          )}
                          name="remoteLockVisitorAccessAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.remoteLockAvailable
                              ? values.remoteLockVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.remoteLockAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.pointCentralAvailable')}
                          name="pointCentralAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable ? values.pointCentralAvailable : false
                          }
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.pointCentralLocksAvailable',
                          )}
                          name="pointCentralLocksAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.pointCentralAvailable
                              ? values.pointCentralLocksAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.pointCentralAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.pointCentralThermostatsAvailable',
                          )}
                          name="pointCentralThermostatsAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.pointCentralAvailable
                              ? values.pointCentralThermostatsAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.pointCentralAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.accentraAvailable')}
                          name="accentraAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.accentraAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.iglooWorkAvailable')}
                          name="iglooWorkAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.iglooWorkAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label="Igloo Staff Master-key available"
                          name="iglooWorkStaffMasterKeyAvailable"
                          onChange={() => {
                            if (values.iglooWorkStaffMasterKeyAvailable) {
                              setShowIglooWorkStaffMasterKeyConfirm(true)
                            } else {
                              setFieldValue('iglooWorkStaffMasterKeyAvailable', true)
                            }
                          }}
                          value={
                            values.keylessAccessAvailable && values.iglooWorkAvailable
                              ? values.iglooWorkStaffMasterKeyAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.iglooWorkAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        {/* EL-11148: Iglooworks visitor BLE access. Gated on keylessAccess -> iglooWork. Turning
                            off revokes every existing visitor key for the building, so it is confirmed first. */}
                        <ToggleField
                          label="Iglooworks Visitor Access available"
                          name="iglooWorkVisitorAccessAvailable"
                          onChange={() => {
                            if (values.iglooWorkVisitorAccessAvailable) {
                              setShowIglooWorkVisitorAccessConfirm(true)
                            } else {
                              setFieldValue('iglooWorkVisitorAccessAvailable', true)
                            }
                          }}
                          value={
                            values.keylessAccessAvailable && values.iglooWorkAvailable
                              ? values.iglooWorkVisitorAccessAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.iglooWorkAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.iglooWorkCustomPinAvailable',
                          )}
                          name="iglooWorkCustomPinAvailable"
                          onChange={(e) => {
                            handleChange(e)
                            customPinTracking.trackToggle(e.target.checked)
                          }}
                          value={
                            values.keylessAccessAvailable && values.iglooWorkAvailable
                              ? values.iglooWorkCustomPinAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.iglooWorkAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        {/* EL-11401: Iglooworks resident algoPIN. Gated on keylessAccess -> iglooWork. */}
                        <ToggleField
                          label="Unit Igloo PIN code for residents available"
                          name="iglooWorkAlgoPinAvailable"
                          onChange={handleChange}
                          value={
                            values.keylessAccessAvailable && values.iglooWorkAvailable
                              ? values.iglooWorkAlgoPinAvailable
                              : false
                          }
                          disabled={!values.keylessAccessAvailable || !values.iglooWorkAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label="Visionline available"
                          name="visionlineAvailable"
                          onChange={handleChange}
                          value={values.keylessAccessAvailable ? values.visionlineAvailable : false}
                          disabled={!values.keylessAccessAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.doorActivityNotificationsAvailable',
                          )}
                          id="doorActivityNotificationsAvailable"
                          name="doorActivityNotificationsAvailable"
                          onChange={handleChange}
                          value={values.doorActivityNotificationsAvailable}
                        />
                      </div>
                    </div>
                    <br />
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.coolAutomationAvailable')}
                          name="coolAutomationAvailable"
                          onChange={handleChange}
                          value={values.coolAutomationAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.ecobeeAvailable')}
                          name="ecobeeAvailable"
                          onChange={handleChange}
                          value={values.ecobeeAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.honeywellAvailable')}
                          name="honeywellAvailable"
                          onChange={handleChange}
                          value={values.honeywellAvailable}
                        />
                      </div>
                    </div>
                    <h3>{t('organisation:places.building.sections.forResidentsAndManagers')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.communityCalendar')}
                          id="communityCalendar"
                          name="communityCalendar"
                          onChange={handleChange}
                          value={values.communityCalendar}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.groupsAvailable')}
                          id="groupsAvailable"
                          name="groupsAvailable"
                          onChange={handleChange}
                          value={values.groupsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.amenityReservations')}
                          id="amenityReservations"
                          name="amenityReservations"
                          onChange={handleChange}
                          value={values.amenityReservations}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.amenitiesAcrAvailable')}
                          id="amenitiesAcrAvailable"
                          name="amenitiesAcrAvailable"
                          onChange={handleChange}
                          value={
                            values.amenityReservations && values.saltoSpaceAvailable
                              ? values.amenitiesAcrAvailable
                              : false
                          }
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.guestAccess')}
                          id="guestAccess"
                          name="guestAccess"
                          onChange={handleChange}
                          value={values.guestAccess}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.physicalKeyAvailable')}
                          id="physicalKeyAvailable"
                          name="physicalKeyAvailable"
                          onChange={handleChange}
                          value={values.physicalKeyAvailable}
                          disabled={!values.guestAccess}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.customAccessToggle', {
                            name: selectedBuilding.customAccessName,
                          })}
                          id="customAccessAvailable"
                          name="customAccessAvailable"
                          onChange={handleChange}
                          value={values.customAccessAvailable}
                          disabled={!values.guestAccess}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.maintenanceRequestAvailable',
                          )}
                          id="maintenanceRequestAvailable"
                          name="maintenanceRequestAvailable"
                          onChange={handleChange}
                          value={values.maintenanceRequestAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.chatAvailable')}
                          id="chatAvailable"
                          name="chatAvailable"
                          onChange={handleChange}
                          value={values.chatAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.widgetChatAvailable')}
                          id="widgetChatAvailable"
                          name="widgetChatAvailable"
                          onChange={handleChange}
                          value={values.widgetChatAvailable}
                          disabled={!values.chatAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.homepageAvailable')}
                          id="homepageAvailable"
                          name="homepageAvailable"
                          onChange={handleChange}
                          value={values.homepageAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.loyaltyAvailable')}
                          id="loyaltyAvailable"
                          name="loyaltyAvailable"
                          onChange={handleChange}
                          value={values.loyaltyAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.rentAvailable')}
                          id="rentAvailable"
                          name="rentAvailable"
                          onChange={handleChange}
                          value={values.rentAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.maintenanceEscalationAvailable',
                          )}
                          id="maintenanceEscalationAvailable"
                          name="maintenanceEscalationAvailable"
                          onChange={handleChange}
                          value={values.maintenanceEscalationAvailable}
                        />
                      </div>
                    </div>
                    <h3>{t('organisation:places.building.sections.forManagers')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.communicationAvailable')}
                          id="communicationAvailable"
                          name="communicationAvailable"
                          onChange={handleChange}
                          value={values.communicationAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.chatPM')}
                          id="chatPM"
                          name="chatPM"
                          onChange={handleChange}
                          value={values.chatPM}
                          disabled={!values.chatAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.chatFD')}
                          id="chatFD"
                          name="chatFD"
                          onChange={handleChange}
                          value={values.chatFD}
                          disabled={!values.chatAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.chatMT')}
                          id="chatMT"
                          name="chatMT"
                          onChange={handleChange}
                          value={values.chatMT}
                          disabled={!values.chatAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.checklistsAvailable')}
                          id="checklistsAvailable"
                          name="checklistsAvailable"
                          onChange={handleChange}
                          value={values.checklistsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.buildingAnalyticsAvailable',
                          )}
                          id="buildingAnalyticsAvailable"
                          name="buildingAnalyticsAvailable"
                          onChange={handleChange}
                          value={values.buildingAnalyticsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.groupsPostReview')}
                          id="groupsPostReview"
                          name="groupsPostReview"
                          onChange={handleChange}
                          value={values.groupsPostReview}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.manualApproval')}
                          id="manualApproval"
                          name="manualApproval"
                          onChange={handleChange}
                          value={values.manualApproval}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.packagesAvailable')}
                          id="packagesAvailable"
                          name="packagesAvailable"
                          onChange={handleChange}
                          value={values.packagesAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.packagesQuickScanAvailable',
                          )}
                          id="packagesQuickScanAvailable"
                          name="packagesQuickScanAvailable"
                          onChange={handleChange}
                          value={
                            values.packagesAvailable ? values.packagesQuickScanAvailable : false
                          }
                          disabled={!values.packagesAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.packagesD2DAvailable')}
                          id="packagesD2DAvailable"
                          name="packagesD2DAvailable"
                          onChange={handleChange}
                          value={values.packagesD2DAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.managementUsersAvailable')}
                          id="managementUsersAvailable"
                          name="managementUsersAvailable"
                          onChange={handleChange}
                          value={values.managementUsersAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.communicateSMSAvailable')}
                          id="communicateSMSAvailable"
                          name="communicateSMSAvailable"
                          onChange={handleChange}
                          value={values.communicateSMSAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.displayBuildingNumber')}
                          id="displayBuildingNumber"
                          name="displayBuildingNumber"
                          onChange={handleChange}
                          value={values.displayBuildingNumber}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.tvAppAvailable')}
                          id="tvAppAvailable"
                          name="tvAppAvailable"
                          onChange={handleChange}
                          value={values.tvAppAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.packageRoomAvailable')}
                          id="packageRoomAvailable"
                          name="packageRoomAvailable"
                          onChange={handleChange}
                          value={values.packageRoomAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.marketShopAvailable')}
                          id="marketShopAvailable"
                          name="marketShopAvailable"
                          onChange={(e) => {
                            handleChange(e)
                            // Note: If market shop is not available, then market shop restaurant should not be available
                            if (!e.target.checked) {
                              setFieldValue('marketShopRestaurantAvailable', false)
                            }
                          }}
                          value={values.marketShopAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.marketShopRestaurantAvailable',
                          )}
                          id="marketShopRestaurantAvailable"
                          name="marketShopRestaurantAvailable"
                          onChange={handleChange}
                          value={values.marketShopRestaurantAvailable}
                          disabled={!values.marketShopAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.rewardsAvailable')}
                          id="rewardsAvailable"
                          name="rewardsAvailable"
                          onChange={handleChange}
                          value={values.rewardsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.valetAvailable')}
                          id="valetAvailable"
                          name="valetAvailable"
                          onChange={handleChange}
                          value={values.valetAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.parkingManagementAvailable',
                          )}
                          id="parkingManagementAvailable"
                          name="parkingManagementAvailable"
                          onChange={handleChange}
                          value={values.parkingManagementAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.visitorParkingAvailable')}
                          id="visitorParkingAvailable"
                          name="visitorParkingAvailable"
                          onChange={handleChange}
                          value={values.visitorParkingAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.amenityRewardsAvailable')}
                          id="amenityRewardsAvailable"
                          name="amenityRewardsAvailable"
                          onChange={handleChange}
                          value={values.amenityRewardsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.maintenanceCallAvailable')}
                          id="maintenanceCallAvailable"
                          name="maintenanceCallAvailable"
                          onChange={handleChange}
                          value={values.maintenanceCallAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.inspectionsAvailable')}
                          id="inspectionsAvailable"
                          name="inspectionsAvailable"
                          onChange={handleChange}
                          value={values.inspectionsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.reputationAvailable')}
                          id="reputationAvailable"
                          name="reputationAvailable"
                          onChange={handleChange}
                          value={values.reputationAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.eventTicketsAvailable')}
                          id="eventTicketsAvailable"
                          name="eventTicketsAvailable"
                          onChange={handleChange}
                          value={values.eventTicketsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.chatCustom')}
                          id="chatCustom"
                          name="chatCustom"
                          onChange={handleChange}
                          value={values.chatCustom}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.aiWriterAvailable')}
                          id="aiWriterAvailable"
                          name="aiWriterAvailable"
                          onChange={handleChange}
                          value={values.aiWriterAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.amenityLocationAvailable')}
                          id="amenityLocationAvailable"
                          name="amenityLocationAvailable"
                          onChange={handleChange}
                          value={values.amenityLocationAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.amenityMarketplaceAvailable',
                          )}
                          id="amenityMarketplaceAvailable"
                          name="amenityMarketplaceAvailable"
                          onChange={handleChange}
                          value={values.amenityMarketplaceAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.flexibleRoleAvailable')}
                          id="flexibleRoleAvailable"
                          name="flexibleRoleAvailable"
                          onChange={handleChange}
                          value={values.flexibleRoleAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.residentEventsAvailable')}
                          id="residentEventsAvailable"
                          name="residentEventsAvailable"
                          onChange={handleChange}
                          value={values.residentEventsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.incidentReportsAvailable')}
                          id="incidentReportsAvailable"
                          name="incidentReportsAvailable"
                          onChange={handleChange}
                          value={values.incidentReportsAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.assetTrackingAvailable')}
                          id="assetTrackingAvailable"
                          name="assetTrackingAvailable"
                          onChange={handleChange}
                          value={values.assetTrackingAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.actionLogAvailable')}
                          id="actionLogAvailable"
                          name="actionLogAvailable"
                          onChange={handleChange}
                          value={values.actionLogAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.frontDeskAvailable')}
                          id="frontDeskAvailable"
                          name="frontDeskAvailable"
                          onChange={handleChange}
                          value={values.frontDeskAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t(
                            'organisation:places.building.toggles.restaurantManagerAvailable',
                          )}
                          id="restaurantManagerAvailable"
                          name="restaurantManagerAvailable"
                          onChange={handleChange}
                          value={values.restaurantManagerAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.customerPortalAvailable')}
                          id="customerPortalAvailable"
                          name="customerPortalAvailable"
                          onChange={handleChange}
                          value={values.customerPortalAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.qrCodeSurveysAvailable')}
                          id="qrCodeSurveysAvailable"
                          name="qrCodeSurveysAvailable"
                          onChange={handleChange}
                          value={values.qrCodeSurveysAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.appUserStatusAvailable')}
                          id="appUserStatusAvailable"
                          name="appUserStatusAvailable"
                          onChange={handleChange}
                          value={values.appUserStatusAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label={t('organisation:places.building.toggles.websiteWidgetsAvailable')}
                          id="websiteWidgetsAvailable"
                          name="websiteWidgetsAvailable"
                          onChange={handleChange}
                          value={values.websiteWidgetsAvailable}
                        />
                      </div>
                    </div>
                    <h3>{t('organisation:places.building.sections.convenienceFee')}</h3>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <p className="section-info">
                          {t('organisation:places.building.sections.convenienceFeeInfo')}
                        </p>
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label="Events"
                          id="eventConvenienceFeeAvailable"
                          name="eventConvenienceFeeAvailable"
                          onChange={handleChange}
                          value={values.eventConvenienceFeeAvailable}
                        />
                      </div>
                    </div>
                    <div className="columns">
                      <div className="column is-9 toggle">
                        <ToggleField
                          label="Reservations"
                          id="reservationConvenienceFeeAvailable"
                          name="reservationConvenienceFeeAvailable"
                          onChange={handleChange}
                          value={values.reservationConvenienceFeeAvailable}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="columns form__actions">
                  <div className="column">
                    <button
                      type={'submit'}
                      className={`button button-primary ${isSubmitting ? 'is-loading' : ''}`}
                      disabled={!isValid || !dirty || isSubmitting}
                    >
                      {t('common:actions.save')}
                    </button>
                    <label className="help is-danger">{displayErrors(errors)}</label>
                  </div>
                </div>
              </div>
            </div>
            {showIglooWorkVisitorAccessConfirm && (
              <ConfirmPopup
                isOpen
                title="Confirmation"
                body="Turning off Iglooworks Visitor Access will immediately revoke all existing visitor Bluetooth keys for this building. This action cannot be undone. Do you want to proceed?"
                confirmText="Yes, turn off"
                cancelText="Cancel"
                confirmCallback={() => {
                  setFieldValue('iglooWorkVisitorAccessAvailable', false)
                  setShowIglooWorkVisitorAccessConfirm(false)
                }}
                cancelCallback={() => setShowIglooWorkVisitorAccessConfirm(false)}
              />
            )}
            {showIglooWorkStaffMasterKeyConfirm && (
              <ConfirmPopup
                isOpen
                title="Confirmation"
                body="Turning off Igloo Staff Master-key will immediately revoke all self-issued staff Bluetooth keys for this building, and staff will no longer be able to request access. This action cannot be undone. Do you want to proceed?"
                confirmText="Yes, turn off"
                cancelText="Cancel"
                confirmCallback={() => {
                  setFieldValue('iglooWorkStaffMasterKeyAvailable', false)
                  setShowIglooWorkStaffMasterKeyConfirm(false)
                }}
                cancelCallback={() => setShowIglooWorkStaffMasterKeyConfirm(false)}
              />
            )}
            {pendingKeylessStaffAccess !== null && (
              <ConfirmPopup
                isOpen
                title="Confirmation"
                body={
                  pendingKeylessStaffAccess
                    ? 'Turning on Keyless access for staff will also enable Iglooworks staff access for this property. Iglooworks Bluetooth keys cannot be recalled once issued. Do you want to proceed?'
                    : "Turning off Keyless access for staff will also disable Iglooworks staff access, every existing staff member won't be able to use it's Bluetooth key for this building. Activating this feature again will return the existing keys to the staff members. Do you want to proceed?"
                }
                confirmText={pendingKeylessStaffAccess ? 'Yes, turn on' : 'Yes, turn off'}
                cancelText="Cancel"
                confirmCallback={() => {
                  setFieldValue('keylessAccessForStaffAvailable', pendingKeylessStaffAccess)
                  setPendingKeylessStaffAccess(null)
                }}
                cancelCallback={() => setPendingKeylessStaffAccess(null)}
              />
            )}
          </form>
        )
      }}
    </Formik>
  )
}

OrganisationBuildingForm.propTypes = {
  params: PropTypes.object,
  selectedBuilding: PropTypes.object.isRequired,
  createBuilding: PropTypes.func.isRequired,
  buildings: PropTypes.array.isRequired,
  updateBuilding: PropTypes.func,
  updateBuildingApartmentsId: PropTypes.func,
}

OrganisationBuildingForm.defaultProps = {
  buildings: [],
}

export default connect(
  (state, { params }) => ({
    selectedBuilding: selectBuildingById(state)(params.buildingId),
    buildings: selectBuildings(state),
  }),
  { createBuilding, updateBuilding, updateBuildingApartmentsId },
)(OrganisationBuildingForm)
